import { useState, useEffect, useContext, useRef } from "react";
import { toast } from "react-toastify";
import { getCurrentDate } from "../../../configs/helpers";
import { fetchedWarehousesBySubsidiary } from "../../../apis/apis.logistics/apisProduct";
import { fetchedStockInOneWarehouse } from "../../../apis/apis.buys/apisPurchase";
import { sendReadjustmentData, downloadReadjustmentReportExcel } from "../../../apis/apis.dispatches/apisDispatch";
import { ContextAuth } from "../../../contexts/ContextAuth";
import { readjustmentModeOptions } from "../../../configs/Data";
import ReactHTMLTableToExcel from "react-html-table-to-excel";

const initialState = {
    warehouseID: 0, vehicleID: 0, vehicleTwoID: 0,
    warehouseName: "", vehicleLicensePlate: "", vehicleTwoLicensePlate: "",
    readjustmentMode: "E", readjustmentMotive: "REAJUSTE DEL SISTEMA",
    supplyingDate: getCurrentDate("-"), userID: 0,
    details: [], comparatives: [],
};

const inputCls = "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
const labelCls = "block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1";
const thCls    = "px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300";
const tdCls    = "px-3 py-2 text-xs border-b border-gray-100 dark:border-gray-700 align-middle";

function warehouseLabel(item) {
    return item.gang.gangID !== 0
        ? `${item.warehouseName} | ${item.gang.gangName}`
        : item.warehouseName;
}

export function ReadjustmentPage() {
    const [readjustmentState, setReadjustmentState] = useState(initialState);
    const [warehouses, setWarehouses]               = useState([]);
    const [filterValue, setFilterValue]             = useState("");
    const [searchApiData, setSearchApiData]         = useState([]);
    const [isSaving, setIsSaving]                   = useState(false);
    const [hideColumn, setHideColumn]               = useState(false);
    const tableRef = useRef(null);

    // ── Report modal ─────────────────────────────────────────────────────────
    const [showReportModal, setShowReportModal]   = useState(false);
    const [isDownloading, setIsDownloading]       = useState(false);
    const [reportData, setReportData]             = useState({
        warehouseID:      0,
        operationAction:  "E",
        readjustmentDate: getCurrentDate("-"),
    });

    const handleOpenReportModal = () => {
        setReportData({
            warehouseID:      readjustmentState.warehouseID,
            operationAction:  readjustmentState.readjustmentMode,
            readjustmentDate: readjustmentState.supplyingDate,
        });
        setShowReportModal(true);
    };

    const handleDownloadReport = async () => {
        if (Number(reportData.warehouseID) === 0) { toast.error("Seleccione el almacén"); return; }
        if (!reportData.readjustmentDate)          { toast.error("Seleccione una fecha"); return; }
        setIsDownloading(true);
        try {
            await downloadReadjustmentReportExcel(reportData);
            toast.success("Reporte descargado exitosamente");
            setShowReportModal(false);
        } catch { toast.error("Error al descargar el reporte"); }
        finally { setIsDownloading(false); }
    };
    const { auth } = useContext(ContextAuth);

    const getWarehouses = async (id) => {
        try { setWarehouses(await fetchedWarehousesBySubsidiary({ subsidiaryID: id })); }
        catch (e) { throw e; }
    };

    const getComparatives = async (warehouseID) => {
        try {
            const response = await fetchedStockInOneWarehouse({ warehouseID });
            const values = Object.values(response);
            setReadjustmentState((s) => ({ ...s, comparatives: values }));
            setSearchApiData(values);
        } catch (e) { throw e; }
    };

    useEffect(() => { getWarehouses(auth.data.subsidiary.subsidiary_id); }, []);

    useEffect(() => {
        if (Number(readjustmentState.warehouseID) > 0)
            getComparatives(readjustmentState.warehouseID);
    }, [readjustmentState.warehouseID]);

    const handleFilter = (e) => {
        const val = e.target.value;
        setFilterValue(val);
        if (val === "") {
            setReadjustmentState((s) => ({ ...s, comparatives: searchApiData }));
        } else {
            const filtered = searchApiData.filter((v) =>
                v.productSku.toLowerCase().includes(val.toLowerCase()) ||
                v.productSaleName.toLowerCase().includes(val.toLowerCase()) ||
                v.productPurchaseName.toLowerCase().includes(val.toLowerCase())
            );
            if (filtered.length > 0)
                setReadjustmentState((s) => ({ ...s, comparatives: filtered }));
        }
    };

    const handleModeChange = (e) => {
        const mode = e.target.value;
        setReadjustmentState((s) => ({
            ...s,
            readjustmentMode: mode,
            comparatives: s.comparatives.map((obj) => {
                obj.quantity = 0;
                obj.remainingQuantityInTariffs = obj.remainingQuantityInTariffs.map((tariff) => {
                    if (mode === "E") obj.quantity = Number(obj.quantity) + Number(tariff.portion) * tariff.quantityMinimum;
                    else              obj.quantity = Number(obj.quantity) - Number(tariff.portion) * tariff.quantityMinimum;
                    return tariff;
                });
                obj.stockA = obj.fixedStockA + Number(obj.quantity);
                return obj;
            }),
        }));
    };

    const handleClickBtnSave = async () => {
        if (!readjustmentState.comparatives.length) { toast.error("Verifique los items a registrar"); return; }
        if (Number(readjustmentState.warehouseID) <= 0) { toast.error("Verifique el almacén"); return; }
        setIsSaving(true);
        try {
            const r = await sendReadjustmentData(readjustmentState);
            if (r.status === 201) toast.success("Operación registrada con éxito!");
            else toast.error("Ocurrió algún error");
            setReadjustmentState(initialState);
            setFilterValue("");
        } catch { toast.error("Error al registrar el reajuste"); }
        finally { setIsSaving(false); }
    };

    const hasNegativeStock = readjustmentState.comparatives.some((item) => item.stockA < 0);
    const canSave = !isSaving && !hasNegativeStock && readjustmentState.comparatives.length > 0 && Number(readjustmentState.warehouseID) > 0;

    return (
        <>
            {/* Page header */}
            <section className="mb-6">
                <div className="max-w-full px-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                            Reajustes
                        </h1>
                        <nav aria-label="breadcrumb">
                            <ol className="flex items-center gap-2 text-sm">
                                <li className="text-blue-600 dark:text-blue-400">Distribuciones</li>
                                <li className="text-gray-400 dark:text-gray-500 select-none">/</li>
                                <li className="text-gray-700 dark:text-gray-300 font-medium">Reajuste</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </section>

            <section>
                <div className="max-w-full px-4 space-y-4">

                    {/* Controls card */}
                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                <i className="fas fa-sliders-h text-violet-500"></i>
                                Configuración de reajuste
                            </h2>
                        </div>
                        <div className="px-5 py-4 space-y-4">

                            {/* Row 1: warehouse, mode, date */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div>
                                    <label className={`${labelCls} text-blue-600 dark:text-blue-400`}>
                                        <i className="fas fa-warehouse mr-1"></i>Almacén Origen
                                    </label>
                                    <select
                                        name="warehouseID"
                                        value={readjustmentState.warehouseID}
                                        onChange={(e) => {
                                            const found = warehouses.find((w) => w.warehouseID == e.target.value);
                                            if (found) setReadjustmentState({
                                                ...readjustmentState,
                                                warehouseID: found.warehouseID,
                                                warehouseName: found.warehouseName,
                                                userID: auth.data.user_id,
                                            });
                                        }}
                                        className={`${inputCls} text-blue-700 dark:text-blue-300 font-medium`}
                                    >
                                        <option value="0">Elegir...</option>
                                        {warehouses.map((item, i) => (
                                            <option key={i} value={item.warehouseID}>{warehouseLabel(item)}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className={labelCls}>
                                        <i className="fas fa-exchange-alt mr-1"></i>Modo de Reajuste
                                    </label>
                                    <select
                                        name="readjustmentMode"
                                        value={readjustmentState.readjustmentMode}
                                        onChange={handleModeChange}
                                        className={inputCls}
                                    >
                                        <option value="0">Elegir...</option>
                                        {readjustmentModeOptions.map((item, i) => (
                                            <option key={i} value={item.id}>{item.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className={labelCls}><i className="fas fa-calendar mr-1"></i>Fecha</label>
                                    <input
                                        type="date"
                                        name="supplyingDate"
                                        value={readjustmentState.supplyingDate}
                                        onChange={(e) => setReadjustmentState({ ...readjustmentState, supplyingDate: e.target.value })}
                                        className={inputCls}
                                    />
                                </div>
                            </div>

                            {/* Row 2: motive */}
                            <div>
                                <label className={labelCls}><i className="fas fa-comment-alt mr-1"></i>Motivo de Reajuste</label>
                                <textarea
                                    rows={2}
                                    value={readjustmentState.readjustmentMotive}
                                    onChange={(e) => setReadjustmentState({ ...readjustmentState, readjustmentMotive: e.target.value })}
                                    className={`${inputCls} resize-none`}
                                />
                            </div>

                            {/* Row 3: actions */}
                            <div className="flex items-center justify-end gap-3">
                                {hasNegativeStock && (
                                    <span className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                        <i className="fas fa-exclamation-triangle"></i>
                                        Stock negativo detectado
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={handleOpenReportModal}
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                >
                                    <i className="fas fa-file-excel"></i>
                                    Generar Reporte Excel
                                </button>
                                <button
                                    type="button"
                                    disabled={!canSave}
                                    onClick={handleClickBtnSave}
                                    className="inline-flex items-center gap-2 rounded-lg bg-violet-600 hover:bg-violet-700 active:bg-violet-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm px-6 py-2.5 transition-colors focus:outline-none focus:ring-2 focus:ring-violet-400"
                                >
                                    {isSaving
                                        ? <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                                        : <i className="fas fa-check-circle"></i>}
                                    {isSaving ? "Registrando..." : "Reajustar"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Products table */}
                    {readjustmentState.comparatives.length > 0 && (
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">

                            {/* Table header */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                                <div>
                                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                                        Productos para reajuste
                                    </h2>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        {readjustmentState.comparatives.length} productos ·{" "}
                                        <span className={readjustmentState.readjustmentMode === "E" ? "text-emerald-600 dark:text-emerald-400 font-medium" : "text-red-600 dark:text-red-400 font-medium"}>
                                            {readjustmentState.readjustmentMode === "E" ? "↑ Entrada" : "↓ Salida"}
                                        </span>
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setHideColumn(!hideColumn)}
                                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs font-medium px-3 py-1.5 transition-colors"
                                    >
                                        <i className={`fas ${hideColumn ? "fa-eye" : "fa-eye-slash"}`}></i>
                                        {hideColumn ? "Mostrar Cantidad" : "Ocultar Cantidad"}
                                    </button>
                                    <ReactHTMLTableToExcel
                                        id="test-table-xls-button"
                                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                        table="table-to-xls"
                                        filename="report_of_readjustments"
                                        sheet="report"
                                        buttonText="↓ Exportar XLS"
                                    />
                                </div>
                            </div>

                            {/* Search */}
                            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                                <input
                                    type="search"
                                    placeholder="Buscar por SKU o producto..."
                                    value={filterValue}
                                    onInput={handleFilter}
                                    onChange={handleFilter}
                                    className={inputCls}
                                />
                            </div>

                            <div className="overflow-x-auto max-h-[65vh] overflow-y-auto bg-white dark:bg-gray-900">
                                <table className="min-w-full text-sm border-separate border-spacing-0" id="table-to-xls" ref={tableRef}>
                                    <thead className="sticky top-0 z-10">
                                        <tr>
                                            <th className={thCls} style={{ width: "7%" }}>Código</th>
                                            <th className={thCls} style={{ width: "6%" }}>Clasif.</th>
                                            <th className={thCls} style={{ width: "9%" }}>SKU</th>
                                            <th className={`${thCls} text-left`} style={{ width: "20%" }}>Producto</th>
                                            <th className={thCls} style={{ width: "9%" }}>Precio</th>
                                            <th className={`${thCls} text-blue-700 dark:text-blue-300`} style={{ width: "7%" }}>
                                                Stock Tot.<br /><span className="text-[10px] font-normal">UND</span>
                                            </th>
                                            <th className={`${thCls} text-amber-700 dark:text-amber-300`} style={{ width: "7%" }}>
                                                Desg.<br /><span className="text-[10px] font-normal">CJ</span>
                                            </th>
                                            <th className={`${thCls} text-amber-700 dark:text-amber-300`} style={{ width: "7%" }}>
                                                Desg.<br /><span className="text-[10px] font-normal">UND</span>
                                            </th>
                                            {!hideColumn && (
                                                <th className={thCls} style={{ width: "20%" }}>Cantidad</th>
                                            )}
                                            <th className={thCls} style={{ width: "8%" }}>Valorizado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {readjustmentState.comparatives.map((item, index) => {
                                            const qty = Number(item.quantity);
                                            const rowBg = qty > 0
                                                ? "bg-amber-50 dark:bg-amber-900/10"
                                                : qty < 0
                                                    ? "bg-red-50 dark:bg-red-900/10"
                                                    : "bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/40";
                                            return (
                                                <tr key={index} className={rowBg}>
                                                    <td className={`${tdCls} text-center font-bold text-gray-700 dark:text-gray-300`}>{item.productCode}</td>
                                                    <td className={`${tdCls} text-center`}>
                                                        <span className="inline-block rounded-full bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 text-xs font-medium px-2 py-0.5">
                                                            {item.productClassificationDisplay}
                                                        </span>
                                                    </td>
                                                    <td className={`${tdCls} text-center font-mono text-gray-600 dark:text-gray-400`}>{item.productSku}</td>
                                                    <td className={`${tdCls} text-left font-medium text-gray-800 dark:text-gray-200`}>{item.productSaleName}</td>
                                                    <td className={`${tdCls} text-right text-gray-600 dark:text-gray-400`}>{Number(item.priceSale).toFixed(6)}</td>
                                                    <td className={`${tdCls} text-center font-bold text-lg text-blue-700 dark:text-blue-300 ${item.stockA < 0 ? "bg-red-50 dark:bg-red-900/20" : ""}`}>
                                                        {item.stockA}
                                                    </td>
                                                    <td className={`${tdCls} text-center font-bold text-lg text-amber-700 dark:text-amber-300`}>
                                                        {Math.floor(Number(item.stockA) / Number(item.quantityMaximum))}
                                                    </td>
                                                    <td className={`${tdCls} text-center font-bold text-lg text-amber-700 dark:text-amber-300`}>
                                                        {Number(item.stockA) % Number(item.quantityMaximum)}
                                                    </td>
                                                    {!hideColumn && (
                                                        <td className={tdCls}>
                                                            {item.remainingQuantityInTariffs.map((tariffObj, ti) => (
                                                                <div key={ti} className="flex items-center gap-1 mb-1 last:mb-0">
                                                                    <span className={`flex-shrink-0 w-10 text-center rounded text-xs font-bold py-1 ${
                                                                        tariffObj.unitName === "CJ"  ? "bg-red-500 text-white" :
                                                                        tariffObj.unitName === "UND" ? "bg-gray-500 text-white" :
                                                                                                       "bg-emerald-500 text-white"
                                                                    }`}>
                                                                        {tariffObj.unitName}
                                                                    </span>
                                                                    <input
                                                                        type="number"
                                                                        onWheel={(e) => e.target.blur()}
                                                                        value={tariffObj.portion}
                                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm text-center px-1 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                                                                        onChange={(e) => {
                                                                            const val = e.target.value;
                                                                            if (Number(val) >= 0) {
                                                                                setReadjustmentState((prev) => ({
                                                                                    ...prev,
                                                                                    comparatives: prev.comparatives.map((obj) => {
                                                                                        if (obj.productID === item.productID) {
                                                                                            item.quantity = 0;
                                                                                            obj.remainingQuantityInTariffs = item.remainingQuantityInTariffs.map((tariff) => {
                                                                                                if (Number(tariff.productTariffID) === Number(tariffObj.productTariffID))
                                                                                                    tariff.portion = val;
                                                                                                if (prev.readjustmentMode === "E")
                                                                                                    item.quantity = Number(item.quantity) + Number(tariff.portion) * tariff.quantityMinimum;
                                                                                                else
                                                                                                    item.quantity = Number(item.quantity) - Number(tariff.portion) * tariff.quantityMinimum;
                                                                                                return tariff;
                                                                                            });
                                                                                            item.stockA = item.fixedStockA + Number(item.quantity);
                                                                                        }
                                                                                        return obj;
                                                                                    }),
                                                                                }));
                                                                            }
                                                                        }}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </td>
                                                    )}
                                                    <td className={`${tdCls} text-right font-semibold text-gray-700 dark:text-gray-300`}>
                                                        {Number(item.priceSale * item.stockA).toFixed(2)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Empty state */}
                    {readjustmentState.comparatives.length === 0 && Number(readjustmentState.warehouseID) > 0 && (
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                            <div className="py-16 text-center">
                                <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
                                    <i className="fas fa-box-open text-gray-400 dark:text-gray-500 text-2xl"></i>
                                </div>
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Sin productos</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">No se encontraron productos para este almacén.</p>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Report modal */}
            {showReportModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    aria-modal="true"
                    role="dialog"
                >
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowReportModal(false)} />
                    <div className="relative z-10 w-full max-w-md flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl overflow-hidden">

                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 bg-cyan-600 dark:bg-cyan-700 flex-shrink-0">
                            <h5 className="text-base font-semibold text-white flex items-center gap-2">
                                <i className="fas fa-file-excel"></i>
                                Reporte de Reajuste
                            </h5>
                            <button type="button" onClick={() => setShowReportModal(false)} className="text-white/80 hover:text-white transition-colors p-1 rounded" aria-label="Cerrar">
                                <i className="fas fa-times text-lg"></i>
                            </button>
                        </div>

                        {/* Body */}
                        <div className="px-6 py-5 space-y-4 bg-white dark:bg-gray-800">

                            {/* Almacén */}
                            <div>
                                <label className={`${labelCls} text-blue-600 dark:text-blue-400`}>
                                    <i className="fas fa-warehouse mr-1"></i>Almacén
                                </label>
                                <select
                                    value={reportData.warehouseID}
                                    onChange={(e) => setReportData({ ...reportData, warehouseID: Number(e.target.value) })}
                                    className={`${inputCls} text-blue-700 dark:text-blue-300 font-medium`}
                                >
                                    <option value={0}>Elegir...</option>
                                    {warehouses.map((item, i) => (
                                        <option key={i} value={item.warehouseID}>
                                            {item.gang.gangID !== 0 ? `${item.warehouseName} | ${item.gang.gangName}` : item.warehouseName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Tipo de reajuste */}
                            <div>
                                <label className={labelCls}>
                                    <i className="fas fa-exchange-alt mr-1"></i>Tipo de reajuste
                                </label>
                                <select
                                    value={reportData.operationAction}
                                    onChange={(e) => setReportData({ ...reportData, operationAction: e.target.value })}
                                    className={inputCls}
                                >
                                    <option value="E">↑ Entrada</option>
                                    <option value="S">↓ Salida</option>
                                </select>
                            </div>

                            {/* Fecha */}
                            <div>
                                <label className={labelCls}>
                                    <i className="fas fa-calendar mr-1"></i>Fecha
                                </label>
                                <input
                                    type="date"
                                    value={reportData.readjustmentDate}
                                    onChange={(e) => setReportData({ ...reportData, readjustmentDate: e.target.value })}
                                    className={inputCls}
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
                            <button type="button" onClick={() => setShowReportModal(false)} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium px-4 py-2 transition-colors">
                                <i className="fas fa-times"></i>Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleDownloadReport}
                                disabled={isDownloading}
                                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            >
                                {isDownloading
                                    ? <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                                    : <i className="fas fa-download"></i>}
                                {isDownloading ? "Descargando..." : "Descargar Reporte"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}